import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTitleContext } from '../contexts/TitleContext';

/**
 * Centralized component for managing document title and SEO meta tags
 * Uses react-helmet-async for consistent meta tag management
 * Supports Open Graph, Twitter Cards, and structured data
 */
const TitleManager = ({ 
  customTitle,
  customDescription,
  customKeywords,
  customImage,
  customUrl,
  articleData,
  noIndex = false,
  canonicalUrl
}) => {
  const {
    title,
    description,
    keywords,
    siteName,
    ogImage,
    twitterCard,
    formattedTitle,
    getFullUrl,
    getStructuredData
  } = useTitleContext();

  // Use custom values or fall back to context values
  const finalTitle = customTitle || formattedTitle;
  const finalDescription = customDescription || description;
  const finalKeywords = customKeywords || keywords;
  const finalImage = customImage || ogImage;
  const finalUrl = customUrl || getFullUrl(window.location.pathname);

  // Generate structured data based on content type
  const structuredData = articleData 
    ? getStructuredData('Article')
    : getStructuredData('WebSite');

  // Enhanced structured data for articles
  if (articleData) {
    structuredData.headline = articleData.title || title;
    structuredData.description = articleData.excerpt || finalDescription;
    structuredData.datePublished = articleData.publishedAt;
    structuredData.dateModified = articleData.updatedAt || articleData.publishedAt;
    structuredData.wordCount = articleData.wordCount;
    structuredData.timeRequired = articleData.readTime;
    
    if (articleData.author) {
      structuredData.author = {
        '@type': 'Person',
        name: articleData.author
      };
    }

    if (articleData.tags && articleData.tags.length > 0) {
      structuredData.keywords = articleData.tags.join(', ');
    }

    if (finalImage) {
      structuredData.image = {
        '@type': 'ImageObject',
        url: getFullUrl(finalImage),
        width: 1200,
        height: 630
      };
    }
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      
      {/* Robots Meta */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={articleData ? "article" : "website"} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {finalImage && (
        <>
          <meta property="og:image" content={getFullUrl(finalImage)} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={finalTitle} />
        </>
      )}
      
      {/* Article-specific Open Graph tags */}
      {articleData && (
        <>
          {articleData.publishedAt && (
            <meta property="article:published_time" content={articleData.publishedAt} />
          )}
          {articleData.updatedAt && (
            <meta property="article:modified_time" content={articleData.updatedAt} />
          )}
          {articleData.author && (
            <meta property="article:author" content={articleData.author} />
          )}
          {articleData.tags && articleData.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      
      {finalImage && (
        <meta name="twitter:image" content={getFullUrl(finalImage)} />
      )}
      
      {/* Additional Twitter tags for articles */}
      {articleData && (
        <>
          <meta name="twitter:label1" content="Reading time" />
          <meta name="twitter:data1" content={articleData.readTime || "5 min read"} />
          {articleData.author && (
            <>
              <meta name="twitter:label2" content="Written by" />
              <meta name="twitter:data2" content={articleData.author} />
            </>
          )}
        </>
      )}
      
      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Favicon and App Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
    </Helmet>
  );
};

export default TitleManager;